/**
 * The Steps panel that floats over the top-left of the canvas.
 *
 * Collapsed it is just a title and a chevron; expanded it reads the flowchart
 * back as prose, one row per step in plan order. A decision's escalations hang
 * underneath it as `2.1`, `2.2` — they are things Radar does, in order, but
 * they are not steps, so they are sub-numbered rather than counted.
 *
 * Anything removable here is removable from its block too; this is the second
 * way in, not the only one.
 */

import { Button, Popconfirm } from 'antd';
import { CloseOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import {
	AOPDraftPlan,
	AOPDraftStep,
	AOPEscalation,
	AOPSuggestion,
	AOPViolation,
} from '../../types/AOPTypes';
import { AOPSuggestionNote, AOPViolationNote, suggestionsFor, violationsFor } from './AOPNotes';
import { escalationSentence, stepNumberOf, stepTitle } from './utils/AOPPlanUtils';
import { AOPPanelResizeHandle } from './AOPPanelResizeHandle';
import { AOP_STEPS_MIN_WIDTH } from './useResizablePanel';

const ROW = 'flex items-start gap-xs border-0 border-b border-solid border-grey-4 px-s py-xs';

const RemoveButton: React.FC<{ title: string; label: string; onConfirm: () => void }> = ({
	title,
	label,
	onConfirm,
}) => (
	<Popconfirm
		title={title}
		okText="Delete"
		okButtonProps={{ danger: true }}
		cancelText="Cancel"
		onConfirm={onConfirm}
	>
		<button
			type="button"
			className="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-sm border border-solid border-transparent bg-transparent text-character-secondary hover:bg-grey-3 hover:text-grey-10"
			aria-label={label}
		>
			<CloseOutlined />
		</button>
	</Popconfirm>
);

interface AOPStepsPanelProps {
	width: number;
	onHandlePointerDown: (event: React.PointerEvent) => void;
	onHandleKeyDown: (event: React.KeyboardEvent) => void;
	plan: AOPDraftPlan;
	suggestions: AOPSuggestion[];
	violations: AOPViolation[];
	onRemoveStep: (stepId: string) => void;
	onRemoveEscalation: (stepId: string, index: number) => void;
	/** A published version is immutable, so its steps can be read but not removed. */
	isReadOnly?: boolean;
}

export const AOPStepsPanel: React.FC<AOPStepsPanelProps> = ({
	plan,
	suggestions,
	violations,
	onRemoveStep,
	onRemoveEscalation,
	isReadOnly,
	width,
	onHandlePointerDown,
	onHandleKeyDown,
}) => {
	const [isOpen, setIsOpen] = useState(false);

	const escalationsOf = (step: AOPDraftStep): AOPEscalation[] =>
		step.type === 'decision' && step.escalations ? step.escalations : [];

	return (
		// Two boxes: the outer one is unclipped so the grip can sit outside the
		// edge it drags, the inner one clips its own rounded corners.
		<div
			className="relative flex min-h-0 flex-col"
			// Collapsed it is a title and a chevron, so it keeps its own width; open
			// it is whatever the reader has dragged it to.
			style={{ width: isOpen ? width : AOP_STEPS_MIN_WIDTH }}
		>
			{isOpen ? (
				<AOPPanelResizeHandle
					edge="right"
					onPointerDown={onHandlePointerDown}
					onKeyDown={onHandleKeyDown}
				/>
			) : null}
			<div className="flex min-h-0 flex-col overflow-hidden rounded border border-solid border-grey-4 bg-grey-1 shadow-md">
				<div
					className={`flex shrink-0 gap-xs px-s py-xs ${
						isOpen ? 'items-start border-0 border-b border-solid border-grey-4' : 'items-center'
					}`}
				>
					<div className="flex flex-col">
						<span className="h5medium text-character-primary">Steps</span>
						{isOpen ? (
							<span className="footnoteregular text-character-secondary">
								The flowchart in plain English, one step at a time.
							</span>
						) : null}
					</div>
					<span className="flex-1" />
					<Button
						type="text"
						aria-label={isOpen ? 'Collapse steps' : 'Expand steps'}
						aria-expanded={isOpen}
						icon={isOpen ? <UpOutlined /> : <DownOutlined />}
						onClick={() => setIsOpen(!isOpen)}
					/>
				</div>

				{isOpen ? (
					<div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
						{plan.steps.map((step, index) => {
							const stepSuggestions = suggestionsFor(suggestions, step.name);
							const stepViolations = violationsFor(violations, step.name);
							const hasNotes = stepSuggestions.length > 0 || stepViolations.length > 0;
							const number = stepNumberOf(index);

							return (
								<React.Fragment key={step.name}>
									{/*
								  Purple marks a step the service had something to say about,
								  so the colour carries meaning rather than marking the level.
								*/}
									<div className={`${ROW} ${hasNotes ? 'bg-purple-0' : ''}`}>
										<span className="footnoteregular w-7 shrink-0 text-character-secondary">
											{number}
										</span>
										<span className="flex min-w-0 flex-1 flex-col gap-2xs">
											<span className="bodyregular text-character-primary">{stepTitle(step)}</span>
											{stepViolations.map((violation, noteIndex) => (
												<AOPViolationNote key={noteIndex} violation={violation} compact />
											))}
											{stepSuggestions.map((suggestion) => (
												<AOPSuggestionNote key={suggestion.id} suggestion={suggestion} compact />
											))}
										</span>
										{isReadOnly ? null : (
											<RemoveButton
												title="Delete this step? Anything pointing at it will need rewiring."
												label={`Delete ${stepTitle(step)}`}
												onConfirm={() => onRemoveStep(step.name)}
											/>
										)}
									</div>

									{escalationsOf(step).map((escalation, escalationIndex) => (
										<div key={escalationIndex} className={ROW}>
											<span className="footnoteregular w-7 shrink-0 text-character-secondary">
												{number}.{escalationIndex + 1}
											</span>
											<span className="bodyregular min-w-0 flex-1 text-character-primary">
												{escalationSentence(escalation)}
											</span>
											{isReadOnly ? null : (
												<RemoveButton
													title="Remove this escalation?"
													label={`Remove escalation ${number}.${escalationIndex + 1}`}
													onConfirm={() => onRemoveEscalation(step.name, escalationIndex)}
												/>
											)}
										</div>
									))}
								</React.Fragment>
							);
						})}

						{plan.steps.length === 0 ? (
							<span className="footnoteregular px-s py-xs text-character-secondary">
								{isReadOnly
									? 'This version has no steps.'
									: 'No steps yet. Drag a block onto the canvas to start.'}
							</span>
						) : null}
					</div>
				) : null}
			</div>
		</div>
	);
};
